import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check as CheckIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  VpnKey as VpnKeyIcon,
} from "@mui/icons-material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";

// Asetukset → Ominaisuudet → Varauskumppanit.
// The workshop connects booking portals (Örum Munhuolto first): creates the API key it
// pastes into the portal, picks the services and mechanics the portal may book, and
// defines its "seuraava vapaa päivä" rule. Backend: partner_booking/views_admin.py.

const RULE_FIELDS = [
  { key: "min_free_mechanics", label: "partnerBooking.rule.minFreeMechanics", min: 1, max: 50 },
  { key: "slot_minutes", label: "partnerBooking.rule.slotMinutes", min: 15, max: 600 },
  { key: "min_free_slots", label: "partnerBooking.rule.minFreeSlots", min: 1, max: 200 },
  { key: "default_booking_minutes", label: "partnerBooking.rule.defaultBookingMinutes", min: 15, max: 720 },
  { key: "lead_hours", label: "partnerBooking.rule.leadHours", min: 0, max: 720 },
  { key: "horizon_days", label: "partnerBooking.rule.horizonDays", min: 1, max: 365 },
];

const linkDraft = (entry) => ({
  enabled: entry.enabled,
  partner_ref: entry.partner_ref || "",
  service_ids: entry.service_ids || [],
  employee_ids: entry.employee_ids || [],
});

const sameDraft = (a, b) => JSON.stringify(a) === JSON.stringify(b);

function toggleId(list, id) {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id].sort((a, b) => a - b);
}

function PartnerBookingSettingsTab({ t, i18n, onDirtyChange }) {
  const { currentOrganization } = useUserStore();
  const organizationId = currentOrganization?.organization_id;

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [ruleDraft, setRuleDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [newKeys, setNewKeys] = useState({});
  const [confirmSlug, setConfirmSlug] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const load = useCallback(async () => {
    if (!organizationId) return;
    try {
      const resp = await axios.get(`/api/partner-booking/?organization=${organizationId}`);
      setData(resp.data);
      setDrafts(Object.fromEntries(resp.data.partners.map((p) => [p.partner.slug, linkDraft(p)])));
      setRuleDraft({ ...resp.data.free_day_rule });
      setLoadError(false);
    } catch (e) {
      console.error("Failed to load partner booking settings", e);
      setLoadError(true);
    }
  }, [organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const showSaved = () => setSnackbar({ open: true, message: t("partnerBooking.saved"), severity: "success" });
  const showError = () => setSnackbar({ open: true, message: t("partnerBooking.saveFailed"), severity: "error" });

  const copy = (value, field) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const locale = i18n?.language === "sv" ? "sv-FI" : i18n?.language === "en" ? "en-GB" : "fi-FI";
  const formatDay = (isoDate) =>
    new Date(`${isoDate}T00:00:00`).toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "numeric" });
  const formatDateTime = (iso) => new Date(iso).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" });

  const updateDraft = (slug, patch) => setDrafts((prev) => ({ ...prev, [slug]: { ...prev[slug], ...patch } }));

  const rulePayload = () =>
    Object.fromEntries(
      Object.entries(ruleDraft || {}).map(([key, value]) => [
        key,
        key === "require_free_bay" ? !!value : Number(value),
      ])
    );

  const ruleValid =
    !!ruleDraft &&
    RULE_FIELDS.every(({ key, min, max }) => {
      const value = Number(ruleDraft[key]);
      return ruleDraft[key] !== "" && Number.isInteger(value) && value >= min && value <= max;
    });

  const dirtySlugs = data
    ? data.partners
        .filter((entry) => drafts[entry.partner.slug] && !sameDraft(drafts[entry.partner.slug], linkDraft(entry)))
        .map((entry) => entry.partner.slug)
    : [];
  const ruleDirty = !!data && !!ruleDraft && !sameDraft(rulePayload(), data.free_day_rule);
  const isDirty = dirtySlugs.length > 0 || ruleDirty;

  const saveLink = async (slug) => {
    await axios.patch(`/api/partner-booking/links/${slug}/?organization=${organizationId}`, drafts[slug]);
  };
  const saveRule = async () => {
    await axios.put(`/api/partner-booking/free-day-rule/?organization=${organizationId}`, rulePayload());
  };

  const run = async (action) => {
    setBusy(true);
    try {
      await action();
      await load();
      showSaved();
      return true;
    } catch (e) {
      console.error("Failed to save partner booking settings", e);
      showError();
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Used by the unsaved-changes guard of the Ominaisuudet tab.
  const saveAll = async () => {
    if (ruleDirty && !ruleValid) return false;
    return run(async () => {
      for (const slug of dirtySlugs) {
        await saveLink(slug);
      }
      if (ruleDirty) await saveRule();
    });
  };
  const saveAllRef = useRef(saveAll);
  saveAllRef.current = saveAll;
  useEffect(() => {
    onDirtyChange?.(isDirty, () => saveAllRef.current());
  }, [isDirty, onDirtyChange]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateKey = async (slug) => {
    setConfirmSlug(null);
    setBusy(true);
    try {
      const resp = await axios.post(
        `/api/partner-booking/links/${slug}/generate-key/?organization=${organizationId}`
      );
      const { api_key: apiKey, ...entry } = resp.data;
      setNewKeys((prev) => ({ ...prev, [slug]: apiKey }));
      setData((prev) => ({
        ...prev,
        partners: prev.partners.map((p) => (p.partner.slug === slug ? entry : p)),
      }));
      updateDraft(slug, { enabled: true });
    } catch (e) {
      console.error("Failed to create partner API key", e);
      showError();
    } finally {
      setBusy(false);
    }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const resp = await axios.post(
        `/api/partner-booking/free-day-rule/preview/?organization=${organizationId}`,
        rulePayload()
      );
      setPreview(resp.data);
    } catch (e) {
      console.error("Failed to preview free days", e);
      showError();
    } finally {
      setBusy(false);
    }
  };

  const copyAdornment = (value, field) => ({
    endAdornment: (
      <InputAdornment position="end">
        <Tooltip title={copiedField === field ? t("Copied!") : t("Copy to clipboard")}>
          <IconButton size="small" onClick={() => copy(value, field)}>
            {copiedField === field ? <CheckIcon color="success" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </InputAdornment>
    ),
  });

  if (loadError) {
    return <Alert severity="error">{t("partnerBooking.loadFailed")}</Alert>;
  }
  if (!data || !ruleDraft) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const confirmPartner = data.partners.find((p) => p.partner.slug === confirmSlug);

  return (
    <Box data-testid="partner-booking-settings">
      <Typography variant="h6" gutterBottom>
        {t("partnerBooking.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("partnerBooking.description")}
      </Typography>

      {data.partners.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("partnerBooking.noPartners")}
        </Alert>
      )}

      {data.partners.map((entry) => {
        const slug = entry.partner.slug;
        const name = entry.partner.name;
        const draft = drafts[slug] || linkDraft(entry);
        const dirty = dirtySlugs.includes(slug);
        const newKey = newKeys[slug];
        return (
          <Paper key={slug} variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }} data-testid={`partner-${slug}`}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {name}
              </Typography>
              {entry.enabled && entry.has_key && <Chip size="small" color="success" label={t("Active")} />}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("partnerBooking.connectHelp", { partner: name })}
            </Typography>

            {/* 1. API key */}
            <Typography variant="subtitle2">{t("partnerBooking.keyTitle")}</Typography>
            {newKey && (
              <Alert severity="warning" sx={{ my: 1 }} data-testid={`partner-${slug}-new-key`}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t("partnerBooking.keyShownOnce", { partner: name })}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={newKey}
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: "monospace", fontSize: "12px", bgcolor: "background.paper" },
                    ...copyAdornment(newKey, `key-${slug}`),
                  }}
                />
              </Alert>
            )}
            {entry.has_key && !newKey && (
              <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
                {t("partnerBooking.keyInUse", { prefix: entry.api_key_prefix })}{" "}
                {entry.key_last_used_at
                  ? t("partnerBooking.keyLastUsed", { when: formatDateTime(entry.key_last_used_at) })
                  : t("partnerBooking.keyNeverUsed")}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 1 }}>
              <Button
                size="small"
                variant={entry.has_key ? "outlined" : "contained"}
                startIcon={entry.has_key ? <RefreshIcon /> : <VpnKeyIcon />}
                disabled={busy}
                onClick={() => (entry.has_key ? setConfirmSlug(slug) : generateKey(slug))}
                data-testid={`partner-${slug}-generate-key`}
              >
                {entry.has_key ? t("partnerBooking.regenerateKey") : t("partnerBooking.generateKey")}
              </Button>
            </Box>

            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={draft.enabled}
                  onChange={(e) => updateDraft(slug, { enabled: e.target.checked })}
                  inputProps={{ "data-testid": `partner-${slug}-enabled` }}
                />
              }
              label={t("partnerBooking.allowBookings", { partner: name })}
            />

            <Divider sx={{ my: 2 }} />

            {/* 2. What the partner may book */}
            <Typography variant="subtitle2">{t("partnerBooking.services")}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t("partnerBooking.servicesHelp")}
            </Typography>
            {data.services.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t("partnerBooking.noServices")}
              </Typography>
            ) : (
              <FormGroup row sx={{ flexWrap: "wrap" }}>
                {data.services.map((svc) => (
                  <FormControlLabel
                    key={svc.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={draft.service_ids.includes(svc.id)}
                        onChange={() => updateDraft(slug, { service_ids: toggleId(draft.service_ids, svc.id) })}
                      />
                    }
                    label={
                      svc.duration_minutes
                        ? t("partnerBooking.serviceWithDuration", { name: svc.name, minutes: svc.duration_minutes })
                        : svc.name
                    }
                  />
                ))}
              </FormGroup>
            )}

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              {t("partnerBooking.mechanics")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("partnerBooking.mechanicsHelp")}
            </Typography>
            <FormGroup row sx={{ flexWrap: "wrap" }}>
              {data.employees.map((emp) => (
                <FormControlLabel
                  key={emp.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={draft.employee_ids.includes(emp.id)}
                      onChange={() => updateDraft(slug, { employee_ids: toggleId(draft.employee_ids, emp.id) })}
                    />
                  }
                  label={emp.name}
                />
              ))}
            </FormGroup>

            <TextField
              fullWidth
              size="small"
              label={t("partnerBooking.partnerRef")}
              helperText={t("partnerBooking.partnerRefHelp", { partner: name })}
              value={draft.partner_ref}
              onChange={(e) => updateDraft(slug, { partner_ref: e.target.value })}
              inputProps={{ maxLength: 100 }}
              sx={{ mt: 2 }}
            />

            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap", mt: 2 }}>
              <Button size="small" disabled={!dirty || busy} onClick={() => updateDraft(slug, linkDraft(entry))}>
                {t("Cancel")}
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={!dirty || busy}
                onClick={() => run(() => saveLink(slug))}
                data-testid={`partner-${slug}-save`}
              >
                {t("Save")}
              </Button>
            </Box>
          </Paper>
        );
      })}

      {/* 3. Seuraava vapaa päivä */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t("partnerBooking.rule.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("partnerBooking.rule.description")}
        </Typography>

        <Box
          sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 2 }}
        >
          {RULE_FIELDS.map(({ key, label, min, max }) => (
            <TextField
              key={key}
              size="small"
              type="number"
              label={t(label)}
              value={ruleDraft[key]}
              onChange={(e) => setRuleDraft((prev) => ({ ...prev, [key]: e.target.value }))}
              inputProps={{ min, max, step: 1, inputMode: "numeric", "data-testid": `rule-${key}` }}
            />
          ))}
        </Box>
        {data.service_bays_enabled && (
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={!!ruleDraft.require_free_bay}
                onChange={(e) => setRuleDraft((prev) => ({ ...prev, require_free_bay: e.target.checked }))}
              />
            }
            label={t("partnerBooking.rule.requireFreeBay")}
          />
        )}
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          {t("partnerBooking.rule.explanation", {
            mechanics: ruleDraft.min_free_mechanics,
            slots: ruleDraft.min_free_slots,
            minutes: ruleDraft.slot_minutes,
          })}
        </Typography>

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap", mt: 2 }}>
          <Button size="small" disabled={!ruleValid || busy} onClick={runPreview} data-testid="rule-preview">
            {t("partnerBooking.rule.preview")}
          </Button>
          <Button
            size="small"
            disabled={!ruleDirty || busy}
            onClick={() => {
              setRuleDraft({ ...data.free_day_rule });
              setPreview(null);
            }}
          >
            {t("Cancel")}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!ruleDirty || !ruleValid || busy}
            onClick={() => run(saveRule)}
            data-testid="rule-save"
          >
            {t("Save")}
          </Button>
        </Box>

        {preview && (
          <Alert
            severity={preview.days.length ? "info" : "warning"}
            sx={{ mt: 2 }}
            data-testid="rule-preview-result"
          >
            {preview.days.length
              ? t("partnerBooking.rule.previewResult", { days: preview.days.map((d) => formatDay(d.date)).join(", ") })
              : t("partnerBooking.rule.previewNone")}
          </Alert>
        )}
      </Paper>

      <Dialog open={!!confirmSlug} onClose={() => setConfirmSlug(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("partnerBooking.regenerateConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("partnerBooking.regenerateConfirmText", { partner: confirmPartner?.partner.name || "" })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSlug(null)}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => generateKey(confirmSlug)}
            data-testid="partner-regenerate-confirm"
          >
            {t("partnerBooking.regenerateKey")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withTranslation()(PartnerBookingSettingsTab);
